import { forwardRef, type InputHTMLAttributes, useId } from "react";
import { cn } from "~/foundation/utils/cn";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  fullWidth?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, fullWidth, className, id, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;

  return (
    <div className={cn("flex flex-col gap-1.5", fullWidth && "w-full")}>
      {label ? (
        <label htmlFor={inputId} className="text-small font-medium text-text-secondary">
          {label}
        </label>
      ) : null}
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          "h-8 rounded-sm border bg-bg px-2.5 text-body text-text",
          "transition-colors duration-100 ease-in-out motion-reduce:transition-none",
          "focus:outline-none focus-visible:outline-none",
          error
            ? "border-danger focus:border-danger"
            : "border-input-border focus:border-input-border-focus",
          "disabled:opacity-50",
          fullWidth && "w-full",
          className,
        )}
        {...rest}
      />
      {error ? (
        <span id={`${inputId}-error`} className="text-small text-danger">
          {error}
        </span>
      ) : hint ? (
        <span id={`${inputId}-hint`} className="text-small text-text-tertiary">
          {hint}
        </span>
      ) : null}
    </div>
  );
});
